import { Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  ModalDialogBody,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";
import { sessionAtom } from "@zapier/canvas-state";

import styles from "./KeyboardShortcutsModal.module.css";

type Props = {
  onClosed: () => void;
};

export default function KeyboardShortcutsModal({ onClosed }: Props) {
  return (
    <ModalDialogRoot isOpen={true} onOpenChange={onClosed}>
      <ModalDialogHeader>
        <Text type="ParagraphHeader3">Keyboard shortcuts</Text>
      </ModalDialogHeader>
      <ModalDialogBody>
        <table className={styles.table}>
          <tbody>
            <tr>
              <td>Zoom in</td>
              <td>
                <div className={styles.flexWrapper}>
                  <span>
                    <span className={styles.key}>⌘ Command</span> +
                  </span>
                  <span className={styles.keyIcon}>+</span>
                </div>
              </td>
            </tr>
            <tr>
              <td>Zoom out</td>
              <td>
                <div className={styles.flexWrapper}>
                  <span>
                    <span className={styles.key}>⌘ Command</span> +
                  </span>
                  <span className={styles.keyIcon}>–</span>
                </div>
              </td>
            </tr>
            <tr>
              <td>Zoom to 100%</td>
              <td>
                <span className={styles.key}>⌘ Command</span> +{" "}
                <span className={styles.key}>0</span>
              </td>
            </tr>
            <tr>
              <td>Scroll to zoom</td>
              <td>
                <span className={styles.key}>⌘ Command</span> + Scroll
              </td>
            </tr>
            <tr>
              <td>Zoom to fit</td>
              <td>
                <span className={styles.key}>⌥ Option</span> +{" "}
                <span className={styles.key}>1</span>
              </td>
            </tr>
            <tr>
              <td>Clean up layout</td>
              <td>
                <span className={styles.key}>⌘ Command</span> +{" "}
                <span className={styles.key}>⇧ Shift</span> +{" "}
                <span className={styles.key}>Y</span>
              </td>
            </tr>
            <tr>
              <td>Undo</td>
              <td>
                <span className={styles.key}>⌘ Command</span> +{" "}
                <span className={styles.key}>Z</span>
              </td>
            </tr>
            <tr>
              <td>Redo</td>
              <td>
                <span className={styles.key}>⌘ Command</span> +{" "}
                <span className={styles.key}>⇧ Shift</span> +{" "}
                <span className={styles.key}>Z</span>
              </td>
            </tr>
            <tr>
              <td>Delete a step</td>
              <td>
                <div>
                  Select step + <span className={styles.key}>Delete</span>
                </div>
                <Spacer height={8} />
                <div>
                  Select step + <span className={styles.key}>Backspace</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <Suspense fallback={null}>
          <StaffShortcuts />
        </Suspense>
        <div className={styles.helpText}>
          <Text type="SmallPrint2" tag="p">
            *For PC: <strong>Control</strong> = <strong>Command</strong> key.{" "}
            <strong>Option</strong> = <strong>Alt</strong> key.{" "}
            <strong>Delete</strong> = <strong>Backspace</strong> key.
          </Text>
        </div>
      </ModalDialogBody>
    </ModalDialogRoot>
  );
}

function StaffShortcuts() {
  const session = useAtomValue(sessionAtom);
  if (!session || !session?.is_staff) {
    return null;
  }
  return (
    <div className={styles.staffShortcuts}>
      <div className={styles.header}>
        <Text type="ParagraphHeader3">Staff shortcuts</Text>
      </div>
      <table className={styles.table}>
        <tbody>
          <tr>
            <td>Toggle DevTools</td>
            <td>
              <span className={styles.key}>⌘ Command</span> +{" "}
              <span className={styles.key}>D</span>
            </td>
          </tr>
          <tr>
            <td>Clear Canvas</td>
            <td>
              <span className={styles.key}>⌘ Command</span> +{" "}
              <span className={styles.key}>⇧ Shift</span> +{" "}
              <span className={styles.key}>C</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
